import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

/* NATION RIVALRY */

const NationInput = z.object({
  nation_code: z.string().min(2).max(6).regex(/^[A-Z]+$/),
  nation_flag: z.string().min(1).max(8),
});

export const setMyNation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => NationInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("user_nations")
      .upsert(
        { user_id: userId, nation_code: data.nation_code, nation_flag: data.nation_flag, updated_at: new Date().toISOString() },
        { onConflict: "user_id" },
      );
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  });

export const getMyNation = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("user_nations")
      .select("nation_code, nation_flag")
      .eq("user_id", userId)
      .maybeSingle();
    return { nation: data ?? null };
  });

const PulseInput = z.object({
  nation_code: z.string().min(2).max(6).regex(/^[A-Z]+$/),
  session_id: z.string().min(1).max(64),
  pulse_count: z.number().int().min(1).max(50),
  match_id: z.string().min(1).max(64).optional(),
});

export const submitNationPulse = createServerFn({ method: "POST" })
  .inputValidator((input) => PulseInput.parse(input))
  .handler(async ({ data }) => {
    const { error } = await supabaseAdmin.from("nation_pulses").insert({
      nation_code: data.nation_code,
      session_id: data.session_id,
      pulse_count: data.pulse_count,
      match_id: data.match_id ?? null,
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  });

export const getNationRivalry = createServerFn({ method: "GET" })
  .handler(async () => {
    const { data, error } = await supabaseAdmin
      .from("nation_rivalry_stats")
      .select("nation_code, hype_total, active_fans")
      .order("hype_total", { ascending: false })
      .limit(20);
    if (error) return { rows: [], error: error.message };
    return { rows: data ?? [], error: null };
  });

/* STREAKS */

const StreakInput = z.object({
  streak_type: z.enum(["prediction", "hype", "login", "referral"]),
  delta: z.number().int().min(-1).max(1).default(1),
});

export const bumpStreak = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => StreakInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("user_streaks")
      .select("id, current_count, best_count")
      .eq("user_id", userId)
      .eq("streak_type", data.streak_type)
      .maybeSingle();
    const nextCount = Math.max(0, (existing?.current_count ?? 0) + data.delta);
    const nextBest = Math.max(existing?.best_count ?? 0, nextCount);
    const { error } = await supabase.from("user_streaks").upsert(
      {
        user_id: userId,
        streak_type: data.streak_type,
        current_count: nextCount,
        best_count: nextBest,
        last_event_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,streak_type" },
    );
    if (error) return { ok: false, error: error.message };
    return { ok: true, current: nextCount, best: nextBest };
  });

export const getMyStreaks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("user_streaks")
      .select("streak_type, current_count, best_count, last_event_at")
      .eq("user_id", userId);
    return { streaks: data ?? [] };
  });

/* FOMO LIVE COUNTERS — single aggregated read, batched */

export const getLivePulseSnapshot = createServerFn({ method: "GET" })
  .handler(async () => {
    const since = new Date(Date.now() - 60_000).toISOString();
    const [{ count: activeFans }, { count: hypeBurst }] = await Promise.all([
      supabaseAdmin
        .from("hype_pulses")
        .select("session_id", { count: "exact", head: true })
        .gt("created_at", since),
      supabaseAdmin
        .from("nation_pulses")
        .select("id", { count: "exact", head: true })
        .gt("created_at", since),
    ]);
    return { active_fans: activeFans ?? 0, hype_burst: hypeBurst ?? 0 };
  });